import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { Button, Card, CardBody, Input, Label } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import { userUpdateRequest } from "store/user/action";
import { userGetRequest } from "store/auth/action";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "store";
import { countriesListGetRequest } from "store/currency/action";
import { isJson } from "utils";
import { withTranslation } from "react-i18next";

const dayGroups: Record<string, string[]> = {
    "monday-friday": ["monday", "tuesday", "wednesday", "thursday", "friday"],
    "saturday-sunday": ["saturday", "sunday"],
    "everyday": ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
};

let settings = { "time_zone": "Asia/Tbilisi", "schedule": [{ "day": "monday", "start": "09:00", "end": "18:00" }, { "day": "tuesday", "start": "09:00", "end": "18:00" }, { "day": "wednesday", "start": "09:00", "end": "18:00" }, { "day": "thursday", "start": "09:00", "end": "18:00" }, { "day": "thursday", "start": "09:00", "end": "18:00" }], "out_of_office_dates": [{ "event": "Test333", "from": "2025-05-23T12:34:35.212Z", "to": "2025-05-23T12:34:35.212Z", "start": "18:30", "end": "00:00" }, { "event": "Test333", "from": "2025-05-23T12:34:45.465Z", "to": "2025-05-23T12:34:45.465Z", "start": "18:30", "end": "00:00" }] }

type WorkingDay = {
    day: string;
    start: string;
    end: string;
};

interface Timezone {
    zoneName: string;
    gmtOffset: number;
    gmtOffsetName: string;
    abbreviation: string;
    tzName: string;
}

const WorkingHoursForm = (props: any) => {
    const { user, view } = props;
    const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
    const timeZoneList = useSelector((state: ApplicationState) => state.currency.list);
    const getFormattedTimeZoneCity = () => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; // e.g., "Asia/Kolkata"
        const parts = tz.split("/");
        if (parts.length < 2) return tz; // fallback if format is unusual

        let city = parts[1].replace(/_/g, " ");
        let area = parts[0].replace(/_/g, " ");
        if (city.toLowerCase() === "calcutta") city = "Kolkata";

        // Capitalize each word in the city name
        city = city.split(" ").map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(" ");
        return `${area}/${city}`;
    };


    const [timeZone, setTimeZone] = useState("");
    const [disabled, setDisabled] = useState(false);
    const [dates, setDates] = useState<any>([]);
    const [schedule, setSchedule] = useState<WorkingDay[]>([]);
    const [countryData, setCountryData] = useState<Record<string, Timezone[]>>({});
    const [groupOptions, setGroupOptions] = useState<{ label: string; value: string; options: { value: string; label: string }[] }[]>([]);

    const customStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : undefined,
            "&:hover": {
                borderColor: "#0d6efd"
            },
            minHeight: "38px",
            borderRadius: "0.375rem",
            cursor: 'pointer'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? "#0d6efd"
                : state.isFocused
                    ? "#e9f3ff"
                    : "white",
            color: state.isSelected ? "white" : "black",
            cursor: "pointer",
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: "#212529",
        }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 9999,
            cursor: 'pointer'
        }),
    };



    const rawUserJson = useMemo(() => {
        const tenantId = user?.tenant?.id;
        if(view) {
            const ut = user?.userTenants?.[0];
            return ut?.workingHoursSettings ?? null 
        }
        else {
            const ut = user?.userTenants?.find((x: any) => x.tenant_id === tenantId);
            return ut?.workingHoursSettings ?? null 
        }      // JSON.stringify(settings);
      }, [user]);


    useEffect(() => {
        let parsed: any = {};
        let timeZone = getFormattedTimeZoneCity();
        try {
            parsed = rawUserJson && isJson(rawUserJson) ? JSON.parse(rawUserJson) : rawUserJson;
        } catch { }
        if (parsed) {
            let schedule = parsed?.schedule;
            let dates = parsed?.out_of_office_dates;
            let timeZone: any = parsed?.time_zone;
            setSchedule(schedule || [])
            setDates(dates || [])
            setTimeZone(timeZone)
        }
        else {
            if (!view) {
                setTimeZone(timeZone);
            }
        }
    }, [rawUserJson, groupOptions]);



    useEffect(() => {
        const filter = {
            "filters": [
                {
                    "search": null
                }
            ],
            "sorts": [
                {
                    "field": "createdAt",
                    "order": "desc"
                }
            ]
        }
        dispatch(countriesListGetRequest(filter, 0, 500, null, 'field'))
    }, [])

    useEffect(() => {
        if (timeZoneList?.length) {
            const categorizedTimezones: Record<string, Timezone[]> = {};
            timeZoneList.forEach((country: any) => {
                try {
                    const timezones: Timezone[] = JSON.parse(country.valuesJson.timezones);
                    timezones.forEach((timezone) => {
                        const continent = timezone.zoneName.split("/")[0];

                        if (!categorizedTimezones[continent]) {
                            categorizedTimezones[continent] = [];
                        }
                        const isTimezoneExists = categorizedTimezones[continent].some(
                            (existingTimezone) => existingTimezone.zoneName === timezone.zoneName
                        );
                        if (!isTimezoneExists) {
                            categorizedTimezones[continent].push(timezone);
                        }
                    });
                } catch (error) {
                    console.error(`Error parsing timezones for country ${country.id}:`, error);
                }
            });

            setCountryData(categorizedTimezones);
        }
    }, [timeZoneList]);


    useEffect(() => {
        const formattedOptions: any = Object.entries(countryData).map(([continent, timezones]) => ({
            label: continent, // Group name
            options: timezones.map((tz) => ({
                value: tz.zoneName,
                label: `${tz.zoneName} ${tz.gmtOffsetName} (${tz.abbreviation})`, // Display with abbreviation
            })),
        }));
        setGroupOptions(formattedOptions);
    }, [countryData]);


    const handleTimeChange = (index: number, key: "start" | "end" | "day", obj: any) => {
        let newData = [...schedule];
        if (index > -1) {
            newData[index] = { ...newData[index], [key]: obj?.value || obj }
        }
        setSchedule(newData);
    };

    const handleDateEventrange = (index: number, key: "start" | "end" | "event" | "from" | "to", obj: any) => {
        let newData = [...dates];
        if (index > -1) {
            newData[index] = { ...newData[index], [key]: obj?.value || obj }
            if (key === "start") {
                const [hourStr, minStr] = (obj?.value || obj).split(":");
                let hour = parseInt(hourStr);
                let minute = parseInt(minStr);

                minute += 30;
                if (minute >= 60) {
                    hour += 1;
                    minute = 0;
                }

                // Wrap around 24h
                if (hour >= 24) hour = 0;

                const nextTime = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
                newData[index].end = nextTime;
            }
        }
        setDates(newData);
    };

    const handleScheduleRemove = (indexToRemove: number) => {
        const updated = [...schedule];
        updated.splice(indexToRemove, 1);
        setSchedule(updated);
    };

    const handleDateRemove = (indexToRemove: number) => {
        const updated = [...dates];
        updated.splice(indexToRemove, 1);
        setDates(updated);
    };

    const daysOptions = [
        {
            label: 'Monday-Friday',
            value: 'monday-friday'
        },
        {
            label: 'Saturday-Sunday',
            value: 'saturday-sunday'
        },
        {
            label: 'Every day',
            value: 'everyday'
        },
        {
            label: 'Monday',
            value: 'monday'
        },
        {
            label: 'Tuesday',
            value: 'tuesday'
        },
        {
            label: 'Wednesday',
            value: 'wednesday'
        },
        {
            label: 'Thursday',
            value: 'thursday'
        },
        {
            label: 'Friday',
            value: 'friday'
        },
        {
            label: 'Saturday',
            value: 'saturday'
        },
        {
            label: 'Sunday',
            value: 'sunday'
        }
    ]

    const getAllSelectedDays = (schedule: WorkingDay[], excludeIndex: number): string[] => {
        const selected: string[] = [];
        schedule.forEach((item, index) => {
            if (index === excludeIndex) return; // skip current row
            const mapped = dayGroups[item?.day] || [item?.day];
            selected.push(...mapped);
        });
        return selected;
    };

    const addSlots = () => {
        const defaultDay: WorkingDay = {
            day: daysOptions?.[0]?.value,
            start: "09:00",
            end: "18:00",
        };
        setSchedule((prevSchedule: any) => {
            return [...prevSchedule, defaultDay]
        })
    }

    const addDateSlots = () => {
        const now = new Date();
        const currentDate = now.toISOString();
        const pad = (num: number) => num.toString().padStart(2, "0");
        let hours = now.getHours();
        let minutes = now.getMinutes();
        // Round to next half hour
        if (minutes < 30) {
            minutes = 30;
        } else {
            minutes = 0;
            hours += 1;
        }
        // Handle overflow
        if (hours === 24) {
            hours = 0;
        }
        const currentTime = `${pad(hours)}:${pad(minutes)}`;
        const defaultEventDate: any = {
            event: "",
            from: currentDate,
            to: currentDate,
            start: currentTime,
            end: "00:00",
        };

        setDates((prevSchedule: any) => {
            return [...prevSchedule, defaultEventDate];
        });
    };



    const generateHalfHourlyOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute of [0, 30]) {
                const hour12 = hour % 12 === 0 ? 12 : hour % 12;
                const ampm = hour < 12 ? "AM" : "PM";
                const label = `${hour12}:${minute === 0 ? "00" : "30"} ${ampm}`;
                const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
                options.push({ value, label });
            }
        }
        return options;
    };

    const hourlyTimeOptions = generateHalfHourlyOptions();

    const save = () => {
        let settings = { time_zone: timeZone, schedule: schedule, out_of_office_dates: dates }
        dispatch(
            userUpdateRequest(
                user?.id!,
                {
                    attributes: [
                        { key: 'user_tenant_working_hours_settings', value: JSON.stringify(settings) },
                    ],
                },
                () => {
                    toast('Settings saved.', {
                        position: 'top-center',
                        hideProgressBar: true,
                        className: 'bg-success text-white',
                    });
                    dispatch(userGetRequest(user?.id!));
                },
                (err: { status: any; }) => {
                    toast(`Error: ${err.status}`, {
                        position: 'top-center',
                        hideProgressBar: true,
                        className: 'bg-danger text-white',
                    });
                }
            )
        );
    };

    const findSelectedTimeZoneOption = (timeZone: string) => {
        for (const group of groupOptions) {
            const found = group.options.find(option => option.value === timeZone);
            if (found) return found;
        }
        return null;
    };

    return (
        <Card className="rounded-0 shadow-sm">
            <CardBody className="pb-4">
                <div className="d-flex flex-column gap-3">
                    <div className="flex-grow-1 d-flex flex-column gap-2" style={{ minWidth: '300px' }}>
                        <h4 className="d-flex fw-bold gap-1 mb-0">
                            <i className="ri-time-line" />
                            {props.t("userpreferences.working_hours")}
                        </h4>
                        <p className="text-muted small mb-2">{props.t("userpreferences.timezone_info_message")}</p>
                    </div>
                    <div className="d-flex flex-column gap-3 p-3 rounded bg-light-subtle">
                        {/* Time Zone Selector */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">{props.t("userpreferences.timezone")}</label>
                            <Select
                                options={groupOptions}
                                value={findSelectedTimeZoneOption(timeZone)}
                                onChange={(selected: any) => setTimeZone(selected?.value || "")}
                                className="basic-single"
                                classNamePrefix="select"
                                style={customStyles}
                                isDisabled={view}
                                isSearchable

                            />
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <h5>{props.t("userpreferences.general_working_hours")}</h5>
                            <p>{props.t("userpreferences.communications_will_only_be_sent_to_this_user_during_these_hours")}</p>
                        </div>
                        <Card className="d-flex p-2 border-1 shadow-sm flex-column gap-3">
                            {schedule?.length ? schedule?.map((item: any, index: any) => {
                                const occupiedDays = getAllSelectedDays(schedule, index);
                                const availableOptions = daysOptions.filter((opt) => {
                                    const optionDays = dayGroups[opt.value] || [opt.value];
                                    return optionDays.every(day => !occupiedDays.includes(day)) || opt.value === item?.day;
                                    // return optionDays.every(day => !occupiedDays.includes(day)) || opt.value === item?.day;
                                });

                                return <div className="d-flex flex-column gap-2">
                                    <div className="d-flex gap-2">
                                        <div className="text-start" style={{ minWidth: 180 }}>
                                            <Select
                                                options={daysOptions}
                                                className="border"
                                                value={daysOptions.find((day: any) => day?.value === item?.day)}
                                                onChange={(option: any) => handleTimeChange(index, "day", option)}
                                                isDisabled={view}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center fw-bold" style={{ minWidth: 40 }}>From</div>
                                        <div style={{ minWidth: 120 }}>
                                            <Select
                                                options={hourlyTimeOptions}
                                                className="border"
                                                value={hourlyTimeOptions.find((hour: any) => hour?.value === item?.start)}
                                                onChange={(option: any) => handleTimeChange(index, "start", option)}
                                                isDisabled={view}

                                            />
                                        </div>
                                        <div className="d-flex align-items-center fw-bold" style={{ minWidth: 20 }}>To</div>
                                        <div style={{ minWidth: 120 }}>
                                            <Select
                                                options={hourlyTimeOptions}
                                                className="border"
                                                value={hourlyTimeOptions.find((hour: any) => hour?.value === item?.end)}
                                                onChange={(option: any) => handleTimeChange(index, "end", option)}
                                                isDisabled={view}
                                            />
                                        </div>
                                        {!view && <div className="d-flex align-items-center" style={{ minWidth: 80 }}>
                                            <i className="ri-delete-bin-fill text-danger cursor-pointer"
                                                onClick={() => handleScheduleRemove(index)}
                                            />

                                        </div>}
                                    </div>
                                    <div>
                                        {
                                            index === schedule?.length - 1 && !view ?
                                                <button disabled={disabled} className='d-flex w-auto btn btn-primary btn-sm' onClick={() => addSlots()}>{props.t("userpreferences.add_hours")} + </button>
                                                : null
                                        }
                                    </div>
                                </div>
                            }) : null}
                            <div>
                                {!schedule?.length && !view ?
                                    <button disabled={disabled} className='d-flex w-auto btn btn-primary btn-sm' onClick={() => addSlots()}>{props.t("userpreferences.add_hours")} + </button>
                                    : !schedule?.length && view ? <div className="text-center p-2 rounded bg-light h-100">
                                        <i className="ri-file-info-line fs-2 text-muted mb-2 d-block" />
                                        <h6 className="fw-semibold text-muted">
                                            {props.t("userpreferences.no_working_hours_set")}
                                        </h6>
                                        <p className="text-muted small mb-0">
                                            {props.t("userpreferences.there_is_no_working_hours_set_for_this_users")}
                                        </p>
                                    </div> : null
                                }
                            </div>
                        </Card>


                        <div className="d-flex flex-column gap-2">
                            <h5>{props.t("userpreferences.out_of_office")}</h5>
                            <p>{props.t("userpreferences.future_time_period_for_out_of_office_availability")}</p>
                        </div>
                        <Card className="d-flex p-2 border-1 border primary shadow-sm flex-column gap-4">
                            {dates?.length ? dates?.map((item: any, index: any) => {
                                const occupiedDays = getAllSelectedDays(dates, index);
                                const availableOptions = daysOptions.filter((opt) => {
                                    const optionDays = dayGroups[opt.value] || [opt.value];
                                    return optionDays.every(day => !occupiedDays.includes(day)) || opt.value === item?.day;
                                    // return optionDays.every(day => !occupiedDays.includes(day)) || opt.value === item?.day;
                                });

                                return <div className="d-flex flex-column gap-2">
                                    <div className="d-flex flex-column gap-2">
                                        <div className="d-flex justify-content-start gap-2">
                                            <div className="text-start w-75">
                                                <Label>{props.t("userpreferences.event_name")}</Label>
                                                <div className="d-flex justify-content-center gap-2">
                                                    <Input
                                                        options={availableOptions}
                                                        className="border"
                                                        value={item?.event}
                                                        disabled={view}
                                                        onChange={(e: any) => handleDateEventrange(index, "event", e.target.value)}
                                                    />
                                                    {!view && <div className="d-flex align-items-center" style={{ minWidth: 80 }}>
                                                        <i className="ri-delete-bin-fill text-danger cursor-pointer"
                                                            onClick={() => handleDateRemove(index)}
                                                        />
                                                    </div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <div className="d-flex align-items-center fw-bold" style={{ minWidth: 40 }}>From</div>
                                            <div className="w-25">
                                                <Flatpickr
                                                    className="form-control"
                                                    options={{ dateFormat: "d M Y" }}
                                                    disabled={!!view}
                                                    value={item?.from || null}
                                                    onChange={(dates) => {
                                                        handleDateEventrange(index, "from", dates[0])
                                                    }}
                                                    placeholder={props.t("common.filter.search.select_date_range")}
                                                />
                                            </div>
                                            <div className="d-flex align-items-center fw-bold" style={{ minWidth: 20 }}>To</div>
                                            <div className="w-25">
                                                <Flatpickr
                                                    className="form-control border"
                                                    options={{ dateFormat: "d M Y" }}
                                                    value={item?.to || null}
                                                    disabled={!!view}
                                                    onChange={(dates) => {
                                                        handleDateEventrange(index, "to", dates[0])
                                                    }}
                                                    placeholder={props.t("common.filter.search.select_date_range")}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <div className="d-flex align-items-center fw-bold" style={{ minWidth: 40 }}></div>
                                            <div className="w-25">
                                                <Select
                                                    options={hourlyTimeOptions}
                                                    className="border"
                                                    isDisabled={view}
                                                    value={hourlyTimeOptions.find((hour: any) => hour?.value === item?.start)}
                                                    onChange={(option: any) => handleDateEventrange(index, "start", option)}

                                                />
                                            </div>
                                            <div className="d-flex align-items-center fw-bold" style={{ minWidth: 20 }}></div>
                                            <div className="w-25">
                                                <Select
                                                    options={hourlyTimeOptions}
                                                    className="border"
                                                    isDisabled={view}
                                                    value={hourlyTimeOptions.find((hour: any) => hour?.value === item?.end)}
                                                    onChange={(option: any) => handleDateEventrange(index, "end", option)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {
                                            index === dates?.length - 1 && !view ?
                                                <button disabled={disabled} className='d-flex w-auto btn btn-primary btn-sm' onClick={() => addDateSlots()}>{props.t("userpreferences.add_dates")} + </button>
                                                : null
                                        }
                                    </div>
                                </div>
                            }) : null}
                            <div>
                                {!dates?.length && !view ?
                                    <button disabled={disabled} className='d-flex w-auto  btn btn-primary btn-sm' onClick={() => addDateSlots()}>{props.t("userpreferences.add_dates")} + </button>
                                    : view && !dates?.length ? <div className="text-center p-2 rounded bg-light h-100">
                                        <i className="ri-file-info-line fs-2 text-muted mb-2 d-block" />
                                        <h6 className="fw-semibold text-muted">
                                            {props.t("userpreferences.no_dates_set")}
                                        </h6>
                                        <p className="text-muted small mb-0">
                                            {props.t("userpreferences.out_of_office_dates_info_message")}
                                        </p>
                                    </div> : null
                                }
                            </div>
                        </Card>
                        {!view && <div>
                            <button type="submit" onClick={(e) => save()} className="btn btn-primary mt-3">{props.t("userpreferences.save_schedule")}</button>
                        </div>}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default withTranslation()(WorkingHoursForm);
